import type { ComposioToolSlug } from '../composio/generated-identifiers';
import { OFFERED_EXTERNAL_TOOLKITS } from '../composio/generated-offered-toolkits';

/**
 * Human labels for a batch of external (vendor) tool calls.
 *
 * Lives in `common` because the same phrase is rendered in three places — the
 * chat timeline, the markdown export, and the channel progress line — and three
 * copies of a label table drift. Callers pass the toolkit and slugs recorded on
 * the tool call.
 *
 * Vendor *results* are never read here. Search *arguments* are, through a
 * narrow allowlist — they are what Aline chose to send, already authorized
 * locally before execution, and naming them is what makes a step checkable.
 */

/**
 * Phrased as what the agent is doing, so it reads the same whether the step is
 * in flight or finished and the surrounding UI supplies the tense.
 */
const ACTION_LABELS = {
  FIREFLIES_GET_MEETING_ANALYTICS: 'reading meeting analytics',
  FIREFLIES_GET_MEETING_PARTICIPANTS: 'reading meeting participants',
  FIREFLIES_GET_MEETING_TOPICS: 'reading meeting topics',
  FIREFLIES_GET_TRANSCRIPT_BY_ID: 'reading a transcript',
  FIREFLIES_GET_TRANSCRIPTS: 'listing transcripts',
  FIREFLIES_GET_USER_BY_ID: 'reading a teammate',
  FIREFLIES_GET_USERS: 'reading teammates',
  FIREFLIES_SEARCH_TRANSCRIPTS: 'searching transcripts',
  GMAIL_FETCH_EMAILS: 'reading emails',
  GMAIL_FETCH_MESSAGE_BY_MESSAGE_ID: 'reading a message',
  GMAIL_FETCH_MESSAGE_BY_THREAD_ID: 'reading a thread',
  GMAIL_GET_ATTACHMENT: 'downloading an attachment',
  GMAIL_GET_CONTACTS: 'reading contacts',
  GMAIL_GET_PROFILE: 'reading the mailbox profile',
  GMAIL_LIST_THREADS: 'listing threads',
  GMAIL_SEARCH_PEOPLE: 'searching contacts',
  GMAIL_SEND_EMAIL: 'sending an email',
  GONG_GET_CALL_BY_ID: 'reading a call',
  GONG_GET_CALL_TRANSCRIPT: 'reading a transcript',
  GONG_GET_INTERACTION_STATISTICS: 'reading interaction analytics',
  GONG_GET_USER_BY_ID: 'reading a teammate',
  GONG_LIST_ALL_USERS_V2_USERS: 'reading teammates',
  GONG_RETRIEVE_CALL_DATA_BY_DATE_RANGE_V2_CALLS: 'listing calls',
  GONG_RETRIEVE_FILTERED_CALL_DETAILS: 'reading call details',
  OUTLOOK_DOWNLOAD_OUTLOOK_ATTACHMENT: 'downloading an attachment',
  OUTLOOK_FORWARD_MESSAGE: 'forwarding a message',
  OUTLOOK_GET_MAIL_FOLDER: 'reading a folder',
  OUTLOOK_GET_ME_CONTACTS: 'reading contacts',
  OUTLOOK_GET_MESSAGE: 'reading a message',
  OUTLOOK_GET_PROFILE: 'reading the mailbox profile',
  OUTLOOK_GET_USER_BY_EMAIL: 'looking up a person',
  OUTLOOK_LIST_MAIL_FOLDER_MESSAGES: 'reading emails in a folder',
  OUTLOOK_LIST_MAIL_FOLDERS: 'listing folders',
  OUTLOOK_LIST_MESSAGES: 'reading emails',
  OUTLOOK_LIST_OUTLOOK_ATTACHMENTS: 'listing attachments',
  OUTLOOK_LIST_SENT_ITEMS_MESSAGES: 'reading sent emails',
  OUTLOOK_QUERY_EMAILS: 'searching emails',
  OUTLOOK_REPLY_EMAIL: 'replying to an email',
  OUTLOOK_SEARCH_MESSAGES: 'searching emails',
  OUTLOOK_SEND_EMAIL: 'sending an email',
} satisfies Readonly<Partial<Record<ComposioToolSlug, string>>>;

/**
 * Vendor parameters that describe *what was searched for*, and the words used
 * to say so.
 *
 * An allowlist rather than a filter, for the same reason the mutation
 * classification is: a vendor adds parameters without telling us, and echoing
 * whatever happened to be in the arguments would put unreviewed vendor-shaped
 * text on a card. Anything absent here is simply not shown.
 *
 * These are the arguments Aline *sent*, not the payload it got back. What we
 * asked for is ours to show; what came back is the untrusted half.
 */
const SEARCH_CRITERIA_LABELS: Record<string, string> = {
  from_date: 'from',
  host_email: 'hosted by',
  keyword: 'matching',
  organizer_email: 'organized by',
  organizers: 'organized by',
  participant_email: 'participant',
  participants: 'participant',
  query: 'matching',
  search: 'matching',
  sender: 'from',
  subject: 'subject',
  title: 'title',
  to_date: 'until',
};

/**
 * Free-text criteria are quoted so the reader can see exactly where the term
 * begins and ends — a query with a trailing space or a stray quote is a real
 * bug, and unquoted it looks like a clean search. Identifiers and dates are not
 * text the user phrased, so quoting them only adds noise.
 */
const QUOTED_CRITERIA = new Set(['keyword', 'query', 'search', 'subject', 'title']);

/** Long enough to recognise a query, short enough not to reflow the card. */
const CRITERIA_VALUE_LIMIT = 80;

/**
 * What a batch searched for, as one line, or null when it searched for nothing.
 *
 * A step that says only "listing transcripts" is unfalsifiable — a reader
 * cannot tell a search that found nothing from one that asked the wrong
 * question. Naming the term is what makes the step checkable.
 */
export function getExternalToolCriteriaLabel(
  actions: readonly { readonly arguments: Readonly<Record<string, unknown>> }[]
): string | null {
  const seen = new Set<string>();
  const parts: string[] = [];

  for (const action of actions) {
    for (const [key, label] of Object.entries(SEARCH_CRITERIA_LABELS)) {
      const value = getCriteriaValue(action.arguments[key], QUOTED_CRITERIA.has(key));
      if (!value) continue;

      const part = `${label} ${value}`;
      if (seen.has(part)) continue;

      seen.add(part);
      parts.push(part);
    }
  }

  return parts.length ? parts.join(', ') : null;
}

/**
 * Only scalars are shown. A nested object in a search argument is either a
 * shape we do not understand or one that would not fit on a line, and both are
 * better left out than rendered as `[object Object]`.
 */
function getCriteriaValue(value: unknown, quoted: boolean): string | null {
  /*
   * The plural criteria — `participants`, `organizers` — arrive as arrays of
   * addresses. Each entry is rendered by the same rules, so one bad entry drops
   * itself rather than the whole line.
   */
  if (Array.isArray(value)) {
    const entries = value.flatMap(entry => getCriteriaValue(entry, quoted) ?? []);
    return entries.length ? entries.join(', ') : null;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const collapsed = trimmed.replaceAll(/\s+/gu, ' ');
  const clipped =
    collapsed.length > CRITERIA_VALUE_LIMIT
      ? `${collapsed.slice(0, CRITERIA_VALUE_LIMIT - 1)}\u2026`
      : collapsed;

  return quoted ? `\u201c${clipped}\u201d` : clipped;
}

/** `gmail` → `Connect Gmail` */
export function getExternalConnectTitle(toolkit: string): string {
  return `Connect ${getExternalToolkitLabel(toolkit)}`;
}

/**
 * Says who is asking and what for, because the card carries a link to a domain
 * the reader has no reason to recognize.
 */
export function getExternalConnectDescription(toolkit: string): string {
  return `Aline needs access to your ${getExternalToolkitLabel(toolkit)} account to finish this request. The link opens ${getExternalToolkitLabel(toolkit)}'s own sign-in.`;
}

export function getExternalEnableTitle(toolkit: string): string {
  return `Enable ${getExternalToolkitLabel(toolkit)} tools`;
}

export function getExternalEnableDescription(toolkit: string): string {
  return `This request needs more ${getExternalToolkitLabel(toolkit)} access than is on. Confirm to turn on the tools listed here.`;
}

/** `gmail` → `Gmail`; an unknown toolkit shows its own slug rather than nothing. */
export function getExternalToolkitLabel(toolkit: string): string {
  const normalizedToolkit = toolkit.toLowerCase();
  const offeredToolkit = OFFERED_EXTERNAL_TOOLKITS.find(
    offered => offered.slug === normalizedToolkit
  );
  return offeredToolkit?.name ?? toolkit;
}

/**
 * A batch may execute more than one vendor tool. Distinct actions are joined so
 * the reader sees everything the batch did, deduplicated so a repeated slug
 * does not read as two steps.
 */
export function getExternalToolActionLabel({
  toolSlugs,
  toolkit,
}: {
  toolkit: string;
  toolSlugs: readonly string[];
}): string {
  const actions = [...new Set(toolSlugs.map(toolSlug => getActionLabel(toolSlug, toolkit)))];
  if (actions.length === 0) return 'running an action';

  return actions.join(', ');
}

/**
 * What Aline verified out of the result, phrased so the absence of a finding
 * is stated rather than left blank.
 *
 * "Verified" is used only of the server's own resolution. A reader comparing
 * this line against the assistant's sentence should be able to tell when the
 * prose has named someone the server never resolved.
 */
export function getExternalToolResolutionLabel({
  documentCount,
  userEmails,
}: {
  documentCount: number;
  userEmails: readonly string[];
}): string {
  const parts: string[] = [];
  if (documentCount > 0) {
    parts.push(`imported ${documentCount} ${documentCount === 1 ? 'document' : 'documents'}`);
  }
  parts.push(
    userEmails.length === 0
      ? 'verified no workspace members'
      : `verified ${userEmails.length === 1 ? 'workspace member' : 'workspace members'}: ${userEmails.join(', ')}`
  );

  return `Aline ${parts.join(', ')}.`;
}

/** `Gmail — reading emails` */
export function getExternalToolCallTitle({
  toolSlugs,
  toolkit,
}: {
  toolkit: string;
  toolSlugs: readonly string[];
}): string {
  return `${getExternalToolkitLabel(toolkit)} — ${getExternalToolActionLabel({ toolSlugs, toolkit })}`;
}

/**
 * An unmapped slug is humanised rather than dropped: a new vendor tool should
 * still name itself in the timeline, and `GMAIL_LIST_LABELS` reads well enough
 * as "list labels" that a missing entry is a polish gap, not a blank step.
 *
 * Only the toolkit's own prefix comes off. Cutting at the first underscore
 * would read `ZOHO_MAIL_SEND_EMAIL` as "mail send email".
 */
function getActionLabel(toolSlug: string, toolkit: string): string {
  if (getHasActionLabel(toolSlug)) return ACTION_LABELS[toolSlug];

  const toolkitPrefix = `${toolkit.toUpperCase()}_`;
  const withoutToolkit = toolSlug.startsWith(toolkitPrefix)
    ? toolSlug.slice(toolkitPrefix.length)
    : toolSlug;
  return withoutToolkit.toLowerCase().replaceAll('_', ' ');
}

function getHasActionLabel(toolSlug: string): toolSlug is keyof typeof ACTION_LABELS {
  return Object.hasOwn(ACTION_LABELS, toolSlug);
}
